import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { BarChart3, Info, Pencil, Plus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAdminBrackets,
  useCreateBracket,
  useDeactivateBracket,
  useUpdateBracket,
  useAdminCategories,
} from '@/hooks/useAdmin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import type { InviteCategory, OutreachFollowerBracket } from '@/api/types';
import { creatorTypeLabel } from '@/lib/labels';
import { formatMoney } from '@/lib/money';

interface FormValues {
  category: InviteCategory;
  minFollowers: string;
  rate: string;
}

const EMPTY: FormValues = { category: '', minFollowers: '0', rate: '0' };

interface EditValues {
  minFollowers: string;
  rate: string;
}

export function AdminBrackets(): JSX.Element {
  const brackets = useAdminBrackets();
  const categories = useAdminCategories();
  const create = useCreateBracket();
  const deactivate = useDeactivateBracket();
  const update = useUpdateBracket();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<OutreachFollowerBracket | null>(null);
  const form = useForm<FormValues>({ defaultValues: EMPTY });
  const editForm = useForm<EditValues>({ defaultValues: { minFollowers: '0', rate: '0' } });
  const typeName = useMemo(() => {
    const map = new Map((categories.data ?? []).map((c) => [c.code, c.displayName]));
    return (code: string) => map.get(code) ?? creatorTypeLabel(code);
  }, [categories.data]);
  const describe = (b: OutreachFollowerBracket): string =>
    `${typeName(b.category)} · ${b.minFollowers.toLocaleString()}+ followers`;

  function openEdit(b: OutreachFollowerBracket): void {
    editForm.reset({ minFollowers: String(b.minFollowers), rate: b.rate });
    setEditing(b);
  }

  async function onEdit(values: EditValues): Promise<void> {
    if (!editing) return;
    try {
      await update.mutateAsync({
        id: editing.id,
        body: {
          minFollowers: Number(values.minFollowers),
          rate: values.rate,
        },
      });
      toast.success('Follower bonus updated');
      setEditing(null);
    } catch (err) {
      toast.error(`Couldn't update the follower bonus: ${(err as Error).message}`);
    }
  }

  async function onSubmit(values: FormValues): Promise<void> {
    try {
      await create.mutateAsync({
        category: values.category,
        minFollowers: Number(values.minFollowers),
        rate: values.rate,
      });
      toast.success('Follower bonus added');
      form.reset(EMPTY);
      setShowForm(false);
    } catch (err) {
      toast.error(`Couldn't add the follower bonus: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Follower bonuses"
        description="A one-time bonus once a creator has posted, based on how many followers they have. Each creator type has its own steps; a creator gets the single highest step they reach, not every step below it."
        actions={
          <Button
            onClick={() => {
              form.reset(EMPTY);
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add follower bonus
          </Button>
        }
      />

      <p className="flex max-w-[75ch] items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        Changes also re-price invites that haven&apos;t been paid yet, at the next morning check.
      </p>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>Edit follower bonus · {typeName(editing.category)}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editForm.handleSubmit(onEdit)} className="grid gap-3" noValidate>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label htmlFor="editMinFollowers">At least this many followers</Label>
                  <Input
                    id="editMinFollowers"
                    inputMode="numeric"
                    {...editForm.register('minFollowers', { required: true })}
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="editRate">Bonus ($)</Label>
                  <Input
                    id="editRate"
                    inputMode="decimal"
                    {...editForm.register('rate', { required: true })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={update.isPending}>
                  Save
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>New follower bonus</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-3" noValidate>
              <div className="grid gap-1">
                <Label>Creator type</Label>
                <Combobox
                  value={form.watch('category')}
                  onChange={(v) =>
                    form.setValue('category', v as FormValues['category'], {
                      shouldValidate: true,
                    })
                  }
                  options={(categories.data ?? [])
                    .filter((c) => c.isActive)
                    .map((c) => ({
                      value: c.code,
                      label: c.displayName,
                    }))}
                  placeholder="Pick a creator type"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label htmlFor="minFollowers">At least this many followers</Label>
                  <Input
                    id="minFollowers"
                    inputMode="numeric"
                    {...form.register('minFollowers', { required: true })}
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="rate">Bonus ($)</Label>
                  <Input
                    id="rate"
                    inputMode="decimal"
                    {...form.register('rate', { required: true })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <DataTable>
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell>Creator type</DataTableHeaderCell>
            <DataTableHeaderCell numeric>Followers (at least)</DataTableHeaderCell>
            <DataTableHeaderCell numeric>Bonus</DataTableHeaderCell>
            <DataTableHeaderCell />
          </tr>
        </DataTableHeader>
        <tbody>
          {(brackets.data ?? []).map((b) => (
            <DataTableRow key={b.id}>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-2 font-medium">
                  <BarChart3
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    strokeWidth={2}
                    aria-hidden
                  />
                  {typeName(b.category)}
                </span>
              </td>
              <td className="px-3 py-2 text-right" data-label="Followers (at least)">
                {b.minFollowers.toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right" data-label="Bonus">${formatMoney(b.rate)}</td>
              <td className="actions px-3 py-2 text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Edit ${describe(b)} bonus`}
                    title="Edit the follower count and bonus amount."
                    onClick={() => openEdit(b)}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <ConfirmDialog
                    title={`Turn off the ${describe(b)} bonus?`}
                    description={
                      <>
                        Stops the ${formatMoney(b.rate)} bonus from applying. Creators at this step
                        fall back to the next step down, if there is one. Amounts already paid stay
                        paid; invites that haven&apos;t been paid yet are re-priced at the next
                        morning check. Add it again to bring it back.
                      </>
                    }
                    tone="destructive"
                    confirmLabel="Turn off bonus"
                    pending={deactivate.isPending}
                    onConfirm={() =>
                      deactivate.mutateAsync(b.id).then(
                        () => {
                          toast.success('Follower bonus turned off');
                        },
                        (err: Error) => {
                          toast.error(`Couldn't turn off the follower bonus: ${err.message}`);
                        },
                      )
                    }
                    trigger={
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        aria-label={`Turn off ${describe(b)} bonus`}
                        title="Stop applying this bonus."
                      >
                        <XCircle className="h-3.5 w-3.5" /> Turn off
                      </Button>
                    }
                  />
                </div>
              </td>
            </DataTableRow>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
}
